import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, Tag, Select, Space, Typography, Card, Tooltip,
} from 'antd';
import { theme as antdTheme } from 'antd';
import axios from 'axios';
import AdminDateFilter, { resolveRange } from '../../components/AdminDateFilter';

const { Title, Text } = Typography;
const { Option } = Select;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  sent:    'blue',
  opened:  'green',
  clicked: 'purple',
  bounced: 'red',
};

const fmt = d => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const AdminEmailLogs = () => {
  const { token: tk } = antdTheme.useToken();

  const [data, setData]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [statusF, setStatusF]         = useState('');
  const [orgFilter, setOrgF]          = useState('');
  const [orgs, setOrgs]               = useState([]);
  const [preset, setPreset]           = useState('All');
  const [customRange, setCustomRange] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const { from, to } = resolveRange(preset, customRange);
      const params = { page: p, limit: 100 };
      if (statusF)   params.status = statusF;
      if (orgFilter) params.org_id = orgFilter;
      if (from)      params.from   = from;
      if (to)        params.to     = to;
      const res = await axios.get('/api/admin/email-logs', { headers: authHeader(), params });
      setData(res.data.data);
      setTotal(res.data.total);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [statusF, orgFilter, preset, customRange]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    axios.get('/api/admin/orgs', { headers: authHeader() })
      .then(r => setOrgs(r.data)).catch(() => {});
  }, []);

  const columns = [
    {
      title: 'Recipient',
      key: 'recipient',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{r.email}</Text>
          {r.name !== '—' && <Text type="secondary" style={{ fontSize: 12 }}>{r.name}</Text>}
        </Space>
      ),
    },
    {
      title: 'Campaign',
      dataIndex: 'campaign_name',
      key: 'campaign',
      render: v => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    {
      title: 'Org',
      dataIndex: 'org_name',
      key: 'org',
      width: 160,
      render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Sent',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 150,
      render: fmt,
    },
    {
      title: 'Opened',
      dataIndex: 'opened_at',
      key: 'opened_at',
      width: 150,
      render: d => <Text style={{ color: d ? '#10b981' : tk.colorTextQuaternary }}>{fmt(d)}</Text>,
    },
    {
      title: 'Bounced',
      dataIndex: 'bounced_at',
      key: 'bounced_at',
      width: 150,
      render: d => <Text style={{ color: d ? '#ef4444' : tk.colorTextQuaternary }}>{fmt(d)}</Text>,
    },
  ];

  return (
    <div style={{ padding: '0 2px' }}>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ marginBottom: 4 }}>Email Send Logs</Title>
        <Text type="secondary">Every email dispatched across all organisations.</Text>
      </div>

      <AdminDateFilter
        preset={preset}
        setPreset={setPreset}
        customRange={customRange}
        setCustomRange={setCustomRange}
        onRefresh={() => load(1)}
        loading={loading}
      />

      <Card
        style={{ borderRadius: 12, border: `1px solid ${tk.colorBorderSecondary}` }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '14px 20px', borderBottom: `1px solid ${tk.colorBorderSecondary}`,
        }}>
          <Select
            value={statusF || 'all'}
            onChange={v => setStatusF(v === 'all' ? '' : v)}
            style={{ width: 140 }}
          >
            <Option value="all">All Statuses</Option>
            <Option value="sent">Sent</Option>
            <Option value="opened">Opened</Option>
            <Option value="clicked">Clicked</Option>
            <Option value="bounced">Bounced</Option>
          </Select>
          <Select
            value={orgFilter || 'all'}
            onChange={v => setOrgF(v === 'all' ? '' : v)}
            style={{ width: 200 }}
            showSearch
            optionFilterProp="children"
          >
            <Option value="all">All Organisations</Option>
            {orgs.map(o => <Option key={o.id} value={String(o.id)}>{o.name}</Option>)}
          </Select>
          <Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
            {total.toLocaleString()} records
          </Text>
        </div>

        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          loading={loading}
          size="small"
          pagination={{
            current: page,
            pageSize: 100,
            total,
            onChange: p => load(p),
            showTotal: n => `${n} emails`,
            style: { padding: '12px 20px', margin: 0 },
          }}
          style={{ borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
        scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default AdminEmailLogs;
